import React, { useEffect, useState, useMemo, useCallback } from "react";
import MainLayout from "../components/layout/MainLayout";
import { API_BASE_URL } from "../config";
import { dashboardApi } from "../services/dashboardApi";
import { Link } from "react-router-dom";

// ─── Types ──────────────────────────────────────────────────────────────

interface LibraryCampaign {
  campaignId: string;
  name: string;
  brandName?: string;
  loanType?: string;
  minLoanAmount?: string;
  maxLoanAmount?: string;
  lenderCount?: string;
  keyUsps?: string;
  loanExample?: string;
  advertiser: string;
  geo: string;
  category: string;
  status?: string;
  landingPageUrl?: string;
  affiliateLinkSlug?: string;
  wctAdvertiserNames?: string[];
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  versionCount?: number;
}

interface TextVersion {
  campaignId: string;
  versionId: string;
  subject: string;
  status: string;
}

interface AffLink {
  id: number;
  slug: string;
  name: string;
  network_display: string;
  network_name: string;
  geo: string;
  category: string;
  is_active: boolean;
  clicks_7d: number;
  clicks_30d: number;
  base_url: string;
  campaignId?: string;
}

interface AdvertiserRevenue {
  advertiser: string;
  campaign_name: string;
  geo: string;
  conversions: number;
  commission: number;
}

interface WctAdvertiser {
  advertiser_name: string;
  conversions: number;
  total_revenue: number;
  mapped_campaign: string | null;
  mapped_geo: string | null;
  mapped_category: string | null;
}

interface AuditItem {
  name: string;
  geo: string;
  campaignId: string;
  espSources: string[];
  versionsInEsp: string[];
  sendCount: number;
  latestSend: string;
  inHub: boolean;
  hasText: boolean;
  textVersionCount: number;
  hasWct: boolean;
  hasLinks: boolean;
  missing: string[];
  isComplete: boolean;
}

interface AuditSummary {
  total: number;
  missing_hub: number;
  missing_text: number;
  missing_wct: number;
  missing_links: number;
  complete: number;
}

interface EnrichedCampaign extends LibraryCampaign {
  versions: TextVersion[];
  versionCount: number;
  links: AffLink[];
  activeLink: AffLink | null;
  revenue7d: number;
  conversions7d: number;
  revenue30d: number;
  conversions30d: number;
}

// ─── Constants ──────────────────────────────────────────────────────────

const GEOS = [
  { id: "ALL", label: "All GEOs" },
  { id: "NO", label: "Norway", flag: "no" },
  { id: "SE", label: "Sweden", flag: "se" },
  { id: "FI", label: "Finland", flag: "fi" },
  { id: "DE", label: "Germany", flag: "de" },
];

const CATEGORIES = ["All", "Loan", "Creditcard", "MicroLoan", "Sweepstake", "Insurance", "Energy", "Nutra", "Casino", "Other"];

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  active:   { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  paused:   { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500" },
  archived: { bg: "bg-gray-100",   text: "text-gray-500",    dot: "bg-gray-400" },
  draft:    { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-500" },
};

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDKK(cents: number): string {
  const val = cents / 100;
  return `kr ${val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getStatusStyle(status: string) {
  return STATUS_COLORS[status] || STATUS_COLORS.draft;
}

async function fetchTextLibrary(endpoint: string, query: Record<string, string> = {}): Promise<any> {
  const params = new URLSearchParams(query);
  const url = `${API_BASE_URL}/api/text-library/${endpoint}?${params}`;
  const res = await fetch(url);
  return res.json();
}

async function fetchAffiliateLinks(): Promise<AffLink[]> {
  const res = await fetch(`${API_BASE_URL}/api/affiliate/links`);
  const json = await res.json();
  return json?.data ?? json ?? [];
}

// ─── WCT Advertiser Panel ───────────────────────────────────────────────

function WctAdvertiserPanel({
  campaign,
  allAdvertisers,
  onSave,
  saveMsg,
}: {
  campaign: EnrichedCampaign;
  allAdvertisers: WctAdvertiser[];
  onSave: (names: string[]) => Promise<void>;
  saveMsg: string;
}) {
  const [linked, setLinked] = React.useState<string[]>(campaign.wctAdvertiserNames || []);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Update local state when campaign changes
  React.useEffect(() => {
    setLinked(campaign.wctAdvertiserNames || []);
  }, [campaign.campaignId, campaign.wctAdvertiserNames]);

  // Available = all advertisers not already linked to this campaign
  const available = allAdvertisers.filter(
    (a) => !linked.includes(a.advertiser_name) &&
      a.advertiser_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addAdvertiser = (name: string) => {
    if (!linked.includes(name)) {
      setLinked([...linked, name]);
    }
    setSearchTerm("");
  };

  const removeAdvertiser = (name: string) => {
    setLinked(linked.filter((n) => n !== name));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(linked);
    setSaving(false);
  };

  const hasChanges = JSON.stringify(linked.sort()) !== JSON.stringify((campaign.wctAdvertiserNames || []).sort());

  // Get stats for a linked advertiser
  const getStats = (name: string) => allAdvertisers.find((a) => a.advertiser_name === name);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-sm font-bold text-gray-700">WeCanTrack Advertiser Names</h4>
          <p className="text-xs text-gray-400 mt-0.5">
            Map WCT advertiser names to this campaign for accurate revenue attribution
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saveMsg && <span className="text-xs text-green-600 font-medium">✅ {saveMsg}</span>}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              hasChanges
                ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : `💾 Save (${linked.length} mapped)`}
          </button>
        </div>
      </div>

      {/* Linked Advertisers — Tag Chips */}
      <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] p-3 bg-gray-50 rounded-lg border border-gray-200">
        {linked.length === 0 && (
          <span className="text-sm text-gray-400 italic">No WCT advertisers linked yet — add from the list below</span>
        )}
        {linked.map((name) => {
          const stats = getStats(name);
          return (
            <div
              key={name}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-200 rounded-full text-sm group hover:border-red-300 transition-colors"
            >
              <span className="font-medium text-gray-700 max-w-[300px] truncate">{name}</span>
              {stats && (
                <span className="text-[10px] text-gray-400">
                  ({stats.conversions} conv · kr {Number(stats.total_revenue).toLocaleString("de-DE", { maximumFractionDigits: 0 })})
                </span>
              )}
              <button
                onClick={() => removeAdvertiser(name)}
                className="ml-1 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 transition-colors text-xs"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      {/* Search + Add Dropdown */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search WCT advertisers to add..."
          className="w-full text-sm rounded-lg px-4 py-2.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white"
        />
        {searchTerm && available.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
            {available.slice(0, 20).map((a) => (
              <button
                key={a.advertiser_name}
                onClick={() => addAdvertiser(a.advertiser_name)}
                className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 transition-colors border-b border-gray-50 last:border-0 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-gray-700 truncate max-w-[60%]">{a.advertiser_name}</span>
                <div className="flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
                  <span>{a.conversions} conv</span>
                  <span className="font-medium text-emerald-600">
                    kr {Number(a.total_revenue).toLocaleString("de-DE", { maximumFractionDigits: 0 })}
                  </span>
                  {a.mapped_campaign && (
                    <span className="text-amber-600 font-medium" title={`Currently mapped to: ${a.mapped_campaign}`}>
                      ⚠️ {a.mapped_campaign}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {available.length > 20 && (
              <div className="text-center text-xs text-gray-400 py-2">
                +{available.length - 20} more — refine your search
              </div>
            )}
          </div>
        )}
        {searchTerm && available.length === 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-400">
            No matching WCT advertisers found
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────

export default function CampaignHub() {
  // Data
  const [campaigns, setCampaigns] = useState<EnrichedCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [geoFilter, setGeoFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Detail panel
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailVersions, setDetailVersions] = useState<TextVersion[]>([]);
  const [detailTab, setDetailTab] = useState<"versions" | "links" | "revenue" | "wct">("versions");

  // Create campaign modal
  const [showCreate, setShowCreate] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: "", advertiser: "", geo: "NO", category: "Loan", landingPageUrl: "", description: "" });
  const [saving, setSaving] = useState(false);

  const [editCampaign, setEditCampaign] = useState<EnrichedCampaign | null>(null);
  const [editForm, setEditForm] = useState({
    campaignId: "",
    name: "",
    brandName: "",
    advertiser: "",
    geo: "NO",
    category: "Loan",
    loanType: "",
    minLoanAmount: "",
    maxLoanAmount: "",
    lenderCount: "",
    keyUsps: "",
    loanExample: "",
    landingPageUrl: "",
    description: "",
  });
  const [editSaving, setEditSaving] = useState(false);

  // WCT advertiser mapping
  const [allWctAdvertisers, setAllWctAdvertisers] = useState<WctAdvertiser[]>([]);
  const [wctSaveMsg, setWctSaveMsg] = useState('');

  // Backfill from ESPs
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<any>(null);
  const [backfillDone, setBackfillDone] = useState(false);
  const [selectedBackfill, setSelectedBackfill] = useState<Set<string>>(new Set());
  const [backfillProgress, setBackfillProgress] = useState('');

  // Campaign Audit
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditItems, setAuditItems] = useState<AuditItem[]>([]);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [auditFilter, setAuditFilter] = useState<'all' | 'missing_hub' | 'missing_text' | 'missing_wct' | 'missing_links' | 'complete'>('all');
  const [auditGeo, setAuditGeo] = useState('ALL');

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const params = auditGeo !== 'ALL' ? `?geo=${auditGeo}` : '';
      const res = await fetch(`${API_BASE_URL}/api/text-library/campaign-audit${params}`);
      const data = await res.json();
      setAuditItems(data.items || []);
      setAuditSummary(data.summary || null);
    } catch (err) {
      console.error('Audit failed:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  const runBackfill = async (dryRun: boolean) => {
    setBackfillLoading(true);
    setBackfillDone(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/text-library/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun, geos: ['NO', 'SE', 'FI', 'DE'] }),
      });
      const data = await res.json();
      setBackfillResult(data);
      if (!dryRun) {
        setBackfillDone(true);
        loadData(); // Reload campaigns
      }
    } catch (err) {
      console.error('Backfill failed:', err);
      setBackfillResult({ error: 'Network error' });
    } finally {
      setBackfillLoading(false);
    }
  };

  // ─── Data Loading ───────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch all data in parallel
      const [campaignsRes, linksRaw, wctRes7, wctRes30, wctAdvRes] = await Promise.all([
        fetchTextLibrary("campaigns"),
        fetchAffiliateLinks(),
        dashboardApi.getWctDashboard("all", "all", 7, false),
        dashboardApi.getWctDashboard("all", "all", 30, false),
        fetch(`${API_BASE_URL}/api/metrics/wct_advertisers`).then(r => r.json()).catch(() => ({ advertisers: [] })),
      ]);

      const libCampaigns: LibraryCampaign[] = campaignsRes.campaigns || [];
      const links: AffLink[] = Array.isArray(linksRaw) ? linksRaw : [];
      const advReport7: AdvertiserRevenue[] = wctRes7.advertiser_report || [];
      const advReport30: AdvertiserRevenue[] = wctRes30.advertiser_report || [];
      const wctAdvertisers: WctAdvertiser[] = wctAdvRes.advertisers || [];
      setAllWctAdvertisers(wctAdvertisers);

      // Enrich each campaign
      const enriched: EnrichedCampaign[] = libCampaigns.map((c) => {
        // Match affiliate links (by name matching for now — will use campaignId later)
        const campaignLinks = links.filter((l) => {
          if (l.campaignId && l.campaignId === c.campaignId) return true;
          // Fuzzy match: link name contains campaign advertiser name
          const advLower = (c.advertiser || c.name || "").toLowerCase();
          return l.name.toLowerCase().includes(advLower) || l.slug.toLowerCase().includes(advLower.replace(/\s+/g, "-"));
        });

        // Match WCT revenue by exact wctAdvertiserNames
        const wctNames = (c.wctAdvertiserNames && c.wctAdvertiserNames.length > 0)
          ? c.wctAdvertiserNames
          : [c.advertiser, c.name].filter(Boolean);

        const matchRevenue = (report: AdvertiserRevenue[]) => {
          let totalRev = 0;
          let totalConv = 0;
          for (const row of report) {
            const rowAdv = row.advertiser || '';
            if (wctNames.some((n) => n === rowAdv)) {
              totalRev += row.commission || 0;
              totalConv += row.conversions || 0;
            }
          }
          return { revenue: totalRev, conversions: totalConv };
        };

        const rev7 = matchRevenue(advReport7);
        const rev30 = matchRevenue(advReport30);

        return {
          ...c,
          status: c.status || "active",
          versions: [],
          versionCount: c.versionCount || 0,
          links: campaignLinks,
          activeLink: campaignLinks.find((l) => l.is_active) || campaignLinks[0] || null,
          revenue7d: rev7.revenue,
          conversions7d: rev7.conversions,
          revenue30d: rev30.revenue,
          conversions30d: rev30.conversions,
        };
      });

      // Sort: active first, then by revenue
      enriched.sort((a, b) => {
        if (a.status !== b.status) {
          const order = ["active", "draft", "paused", "archived"];
          return order.indexOf(a.status || "active") - order.indexOf(b.status || "active");
        }
        return b.revenue30d - a.revenue30d;
      });

      setCampaigns(enriched);
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Filtered campaigns ─────────────────────────────────────────

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (geoFilter !== "ALL" && c.geo?.toUpperCase() !== geoFilter) return false;
      if (categoryFilter !== "All" && c.category?.toLowerCase() !== categoryFilter.toLowerCase()) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          c.name?.toLowerCase().includes(q) ||
          c.advertiser?.toLowerCase().includes(q) ||
          c.campaignId?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [campaigns, geoFilter, categoryFilter, statusFilter, search]);

  // ─── KPI Summary ────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const active = campaigns.filter((c) => c.status === "active").length;
    const paused = campaigns.filter((c) => c.status === "paused").length;
    const totalRev7 = campaigns.reduce((s, c) => s + c.revenue7d, 0);
    const totalConv7 = campaigns.reduce((s, c) => s + c.conversions7d, 0);
    const totalRev30 = campaigns.reduce((s, c) => s + c.revenue30d, 0);
    const totalConv30 = campaigns.reduce((s, c) => s + c.conversions30d, 0);
    return { active, paused, totalRev7, totalConv7, totalRev30, totalConv30, total: campaigns.length };
  }, [campaigns]);

  // ─── Expand detail ──────────────────────────────────────────────

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    setDetailTab("versions");
    setDetailVersions([]); // Clear previous detail versions while loading
    
    try {
      const res = await fetchTextLibrary("versions", { campaignId: id });
      const campaignVersions = res.versions || [];
      setDetailVersions(campaignVersions);
      
      // Update local campaigns list with the loaded versions
      setCampaigns((prev) =>
        prev.map((c) =>
          c.campaignId === id
            ? { ...c, versions: campaignVersions, versionCount: campaignVersions.length }
            : c
        )
      );
    } catch (err) {
      console.error("Failed to load campaign versions on expand:", err);
    }
  };

  // ─── Create Campaign ───────────────────────────────────────────

  const handleCreate = async () => {
    if (!newCampaign.name.trim()) return;
    setSaving(true);
    try {
      const slug = newCampaign.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const campaignId = `${slug}-${newCampaign.geo.toLowerCase()}`;
      await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaignId,
          ...newCampaign,
        }),
      });
      setShowCreate(false);
      setNewCampaign({ name: "", advertiser: "", geo: "NO", category: "Loan", landingPageUrl: "", description: "" });
      loadData();
    } catch (err) {
      console.error("Failed to create campaign:", err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Update status ──────────────────────────────────────────────

  const updateStatus = async (campaignId: string, newStatus: string) => {
    const c = campaigns.find((x) => x.campaignId === campaignId);
    if (!c) return;
    try {
      await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...c, status: newStatus }),
      });
      setCampaigns((prev) =>
        prev.map((x) => (x.campaignId === campaignId ? { ...x, status: newStatus } : x))
      );
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  // ─── Edit Campaign ─────────────────────────────────────────────

  const openEdit = (c: EnrichedCampaign) => {
    setEditCampaign(c);
    setEditForm({
      campaignId: c.campaignId || "",
      name: c.name || "",
      brandName: c.brandName || "",
      advertiser: c.advertiser || "",
      geo: c.geo || "NO",
      category: c.category || "Loan",
      loanType: c.loanType || "",
      minLoanAmount: c.minLoanAmount || "",
      maxLoanAmount: c.maxLoanAmount || "",
      lenderCount: c.lenderCount || "",
      keyUsps: c.keyUsps || "",
      loanExample: c.loanExample || "",
      landingPageUrl: c.landingPageUrl || "",
      description: c.description || "",
    });
  };

  const handleEditSave = async () => {
    if (!editCampaign || !editForm.name.trim() || !editForm.campaignId.trim()) return;
    setEditSaving(true);
    try {
      let finalCampaignId = editCampaign.campaignId;

      // Handle ID rename if it changed
      if (editForm.campaignId !== editCampaign.campaignId) {
        const renameRes = await fetch(`${API_BASE_URL}/api/text-library/campaign/rename`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ oldId: editCampaign.campaignId, newId: editForm.campaignId }),
        });
        const renameData = await renameRes.json();
        if (renameData.error) {
          setError(`Failed to rename: ${renameData.error}`);
          setEditSaving(false);
          return;
        }
        finalCampaignId = editForm.campaignId;
      }

      const payload = {
        campaignId: finalCampaignId,
        name: editForm.name,
        brandName: editForm.brandName,
        advertiser: editForm.advertiser,
        geo: editForm.geo,
        category: editForm.category,
        loanType: editForm.loanType,
        minLoanAmount: editForm.minLoanAmount,
        maxLoanAmount: editForm.maxLoanAmount,
        lenderCount: editForm.lenderCount,
        keyUsps: editForm.keyUsps,
        loanExample: editForm.loanExample,
        landingPageUrl: editForm.landingPageUrl,
        description: editForm.description,
        status: editCampaign.status,
        wctAdvertiserNames: editCampaign.wctAdvertiserNames,
      };
      await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Update local state without full reload
      setCampaigns((prev) =>
        prev.map((x) =>
          x.campaignId === editCampaign.campaignId
            ? { ...x, ...editForm, campaignId: finalCampaignId }
            : x
        )
      );
      setEditCampaign(null);
    } catch (err) {
      console.error("Failed to update campaign:", err);
    } finally {
      setEditSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
              📊 Campaign Hub
            </h2>
            <p className="text-gray-500 mt-1">Manage campaigns, links, text versions & revenue</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0">
            <button
              onClick={() => { setShowAudit(!showAudit); if (!showAudit && auditItems.length === 0) runAudit(); }}
              className={`px-4 py-2 border rounded-lg font-semibold text-sm transition-colors shadow-sm flex items-center gap-2 ${
                showAudit ? 'bg-purple-600 text-white border-purple-600' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
              }`}
            >
              📋 Campaign Audit
            </button>
            <button
              onClick={() => { setShowBackfill(true); setBackfillResult(null); setBackfillDone(false); runBackfill(true); }}
              className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg font-semibold text-sm hover:bg-amber-100 transition-colors shadow-sm flex items-center gap-2"
            >
              🔄 Backfill from ESPs
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
            >
              <span className="text-lg">+</span> New Campaign
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex-wrap">
          <input
            type="text"
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full sm:w-60 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <select
            value={geoFilter}
            onChange={(e) => setGeoFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 flex-1 sm:flex-none min-w-0"
          >
            {GEOS.map((g) => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 flex-1 sm:flex-none min-w-0"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 flex-1 sm:flex-none min-w-0"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <span className="text-sm text-gray-400 ml-auto">{filtered.length} of {campaigns.length} campaigns</span>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { label: "Total", value: kpis.total, color: "text-gray-800", icon: "📚" },
            { label: "Active", value: kpis.active, color: "text-emerald-600", icon: "🟢" },
            { label: "Paused", value: kpis.paused, color: "text-amber-600", icon: "⏸️" },
            { label: "Revenue (7d)", value: formatDKK(kpis.totalRev7), color: "text-emerald-600", icon: "💰" },
            { label: "Conv. (7d)", value: kpis.totalConv7, color: "text-indigo-600", icon: "🎯" },
            { label: "Revenue (30d)", value: formatDKK(kpis.totalRev30), color: "text-emerald-600", icon: "📈" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{kpi.icon} {kpi.label}</div>
              <div className={`text-2xl font-bold ${kpi.color}`}>{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</div>
            </div>
          ))}
        </div>

        {/* ═══ CAMPAIGN AUDIT PANEL ═══ */}
        {showAudit && (
          <div className="bg-white rounded-xl border border-purple-200 shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-purple-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-purple-900">📋 Campaign Audit — What's Missing?</h3>
                <p className="text-sm text-purple-600 mt-0.5">Cross-references ESP campaigns with Campaign Hub, Text Library, WCT mappings, and affiliate links</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={auditGeo} onChange={e => setAuditGeo(e.target.value)} className="text-sm border rounded-lg px-2 py-1">
                  <option value="ALL">All GEOs</option>
                  <option value="NO">🇳🇴 NO</option>
                  <option value="SE">🇸🇪 SE</option>
                  <option value="FI">🇫🇮 FI</option>
                  <option value="DE">🇩🇪 DE</option>
                </select>
                <button onClick={runAudit} disabled={auditLoading} className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50">
                  {auditLoading ? '⏳ Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {auditSummary && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-4 bg-gray-50 border-b">
                {[
                  { label: 'Total Campaigns', value: auditSummary.total, filter: 'all' as const, color: 'text-gray-800', bg: 'bg-white' },
                  { label: 'Missing Hub', value: auditSummary.missing_hub, filter: 'missing_hub' as const, color: 'text-red-700', bg: 'bg-red-50' },
                  { label: 'Missing Text', value: auditSummary.missing_text, filter: 'missing_text' as const, color: 'text-orange-700', bg: 'bg-orange-50' },
                  { label: 'Missing WCT', value: auditSummary.missing_wct, filter: 'missing_wct' as const, color: 'text-amber-700', bg: 'bg-amber-50' },
                  { label: 'Missing Links', value: auditSummary.missing_links, filter: 'missing_links' as const, color: 'text-blue-700', bg: 'bg-blue-50' },
                  { label: 'Complete ✅', value: auditSummary.complete, filter: 'complete' as const, color: 'text-emerald-700', bg: 'bg-emerald-50' },
                ].map(kpi => (
                  <button
                    key={kpi.label}
                    onClick={() => setAuditFilter(kpi.filter)}
                    className={`rounded-lg p-3 text-left border transition-all ${
                      auditFilter === kpi.filter ? 'ring-2 ring-purple-400 border-purple-300 shadow-md' : 'border-gray-200 hover:shadow'
                    } ${kpi.bg}`}
                  >
                    <div className="text-xs font-semibold text-gray-500 uppercase">{kpi.label}</div>
                    <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
                  </button>
                ))}
              </div>
            )}

            {auditLoading ? (
              <div className="p-8 text-center text-gray-500">⏳ Scanning all ESPs and cross-referencing data...</div>
            ) : auditItems.length > 0 ? (
              <div className="max-h-[500px] overflow-auto w-full">
                <table className="w-full min-w-[800px] text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-gray-600">Campaign</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">GEO</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">Hub</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">Text</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">WCT</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">Links</th>
                      <th className="text-left px-4 py-2 font-semibold text-gray-600">ESPs</th>
                      <th className="text-center px-2 py-2 font-semibold text-gray-600">Sends</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-600">Missing</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {auditItems
                      .filter(item => {
                        if (auditFilter === 'all') return true;
                        if (auditFilter === 'complete') return item.isComplete;
                        return item.missing.some(m => m.startsWith(auditFilter.replace('missing_', '')));
                      })
                      .map(item => (
                        <tr key={item.campaignId} className={`hover:bg-gray-50 ${item.isComplete ? 'bg-emerald-50/30' : ''}`}>
                          <td className="px-4 py-2 font-medium text-gray-900">{item.name}</td>
                          <td className="text-center px-2 py-2">
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold">{item.geo}</span>
                          </td>
                          <td className="text-center px-2 py-2">{item.inHub ? '✅' : '❌'}</td>
                          <td className="text-center px-2 py-2">
                            {item.hasText
                              ? <span className="text-emerald-600">✅ {item.textVersionCount}</span>
                              : '❌'
                            }
                          </td>
                          <td className="text-center px-2 py-2">{item.hasWct ? '✅' : '❌'}</td>
                          <td className="text-center px-2 py-2">{item.hasLinks ? '✅' : '❌'}</td>
                          <td className="px-4 py-2">
                            <div className="flex flex-wrap gap-1">
                              {item.espSources.map(esp => (
                                <span key={esp} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-mono">{esp}</span>
                              ))}
                            </div>
                          </td>
                          <td className="text-center px-2 py-2 text-gray-500">{item.sendCount}</td>
                          <td className="px-2 py-2">
                            <div className="flex flex-wrap gap-1">
                              {item.missing.map(m => (
                                <span key={m} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  m.includes('hub') ? 'bg-red-100 text-red-700' :
                                  m.includes('text') ? 'bg-orange-100 text-orange-700' :
                                  m.includes('wct') ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>{m.replace('_', ' ')}</span>
                              ))}
                              {item.missing.length === 0 && <span className="text-emerald-600 text-xs">All good ✨</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : !auditLoading && (
              <div className="p-8 text-center text-gray-400">Click Refresh to load audit data</div>
            )}
          </div>
        )}

        {/* Error / Loading */}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-200">{error}</div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
                <div className="h-5 w-48 bg-gray-200 rounded mb-3" />
                <div className="h-4 w-96 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          /* Campaign Cards */
          <div className="space-y-3">
            {filtered.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="text-4xl mb-4">📭</div>
                <h3 className="text-lg font-semibold text-gray-700">No campaigns found</h3>
                <p className="text-gray-400 mt-1">Try adjusting your filters or create a new campaign.</p>
              </div>
            )}
            {filtered.map((c) => {
              const sty = getStatusStyle(c.status || "active");
              const isExpanded = expandedId === c.campaignId;

              return (
                <div
                  key={c.campaignId}
                  className={`bg-white rounded-xl border transition-all duration-200 ${isExpanded ? "border-indigo-200 shadow-lg ring-1 ring-indigo-100" : "border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200"}`}
                >
                  {/* Card Header */}
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => toggleExpand(c.campaignId)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Name, Tags */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${sty.dot} flex-shrink-0`} />
                          <h3 className="text-lg font-bold text-gray-900 truncate">{c.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sty.bg} ${sty.text}`}>
                            {c.status || "active"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap ml-5">
                          {c.geo && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                              <img src={`https://flagcdn.com/w20/${c.geo.toLowerCase()}.png`} alt={c.geo} className="h-3 w-4 rounded-sm" />
                              {c.geo}
                            </span>
                          )}
                          {c.category && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                              {c.category}
                            </span>
                          )}
                          {c.advertiser && c.advertiser !== c.name && (
                            <span className="text-xs text-gray-400">by {c.advertiser}</span>
                          )}
                          {c.links.length === 0 && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              ⚠️ No Affiliate Link
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Middle: Quick Stats */}
                      <div className="hidden md:flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <div className="text-xs text-gray-400 font-medium">Versions</div>
                          <div className="font-bold text-gray-700">{c.versionCount}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-400 font-medium">Links</div>
                          <div className="font-bold text-gray-700">{c.links.length}</div>
                        </div>
                        {c.activeLink && (
                          <div className="text-center max-w-[120px]">
                            <div className="text-xs text-gray-400 font-medium">Active Link</div>
                            <div className="font-medium text-indigo-600 text-xs truncate">/go/{c.activeLink.slug}/</div>
                          </div>
                        )}
                      </div>

                      {/* Right: Revenue */}
                      <div className="text-right flex-shrink-0">
                        {c.revenue7d > 0 ? (
                          <>
                            <div className="text-lg font-bold text-emerald-600">{formatDKK(c.revenue7d)}</div>
                            <div className="text-xs text-gray-400">{c.conversions7d} conv · 7d</div>
                          </>
                        ) : c.revenue30d > 0 ? (
                          <>
                            <div className="text-lg font-bold text-emerald-600">{formatDKK(c.revenue30d)}</div>
                            <div className="text-xs text-gray-400">{c.conversions30d} conv · 30d</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-300">No revenue yet</div>
                        )}
                        <div className="text-xs text-gray-300 mt-1">
                          {isExpanded ? "▲" : "▼"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail Panel */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/50">
                      {/* Tabs */}
                      <div className="flex gap-1 px-5 pt-3 border-b border-gray-100">
                        {(["versions", "links", "revenue", "wct"] as const).map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setDetailTab(tab)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                              detailTab === tab
                                ? "bg-white text-indigo-700 border border-b-0 border-gray-200 -mb-px"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {tab === "versions" ? `📝 Text Versions (${c.versionCount})` :
                             tab === "links" ? `🔗 Affiliate Links (${c.links.length})` :
                             tab === "revenue" ? "💰 Revenue" :
                             `🔗 WCT Mapping (${(c.wctAdvertiserNames || []).length})`}
                          </button>
                        ))}
                        {/* Quick Actions */}
                        <div className="ml-auto flex items-center gap-2 pb-2 pl-3 flex-wrap justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                            className="px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors border border-amber-200"
                          >
                            ✏️ Edit
                          </button>
                          <Link
                            to="/email/campaigns/auto-scheduler"
                            className="px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                          >
                            📅 Schedule
                          </Link>
                          <select
                            value={c.status || "active"}
                            onChange={(e) => updateStatus(c.campaignId, e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                          >
                            <option value="active">🟢 Active</option>
                            <option value="paused">⏸️ Paused</option>
                            <option value="draft">📝 Draft</option>
                            <option value="archived">📦 Archived</option>
                          </select>
                        </div>
                      </div>

                      <div className="p-5 bg-white rounded-b-xl">
                        {/* Versions Tab */}
                        {detailTab === "versions" && (
                          <div>
                            {c.versions.length === 0 ? (
                              <div className="text-center py-8 text-gray-400">
                                <p>No text versions yet.</p>
                                <Link
                                  to="/email/campaigns/auto-scheduler"
                                  className="text-indigo-600 font-semibold hover:underline mt-2 inline-block"
                                >
                                  Create in Text Library →
                                </Link>
                              </div>
                            ) : (
                              <div className="overflow-x-auto w-full">
                                <table className="w-full min-w-[400px] text-sm">
                                  <thead>
                                    <tr className="text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-100">
                                      <th className="text-left py-3 px-2">Version</th>
                                      <th className="text-left py-3 px-2">Subject</th>
                                      <th className="text-left py-3 px-2">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.versions.map((v) => (
                                      <tr key={v.versionId} className="border-b border-gray-50 hover:bg-gray-50">
                                        <td className="py-3 px-2 font-mono font-bold text-indigo-600">{v.versionId}</td>
                                        <td className="py-3 px-2 text-gray-700 truncate max-w-xs">{v.subject || "—"}</td>
                                        <td className="py-3 px-2">
                                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            v.status === "active" ? "bg-emerald-50 text-emerald-700" :
                                            v.status === "imported" ? "bg-blue-50 text-blue-700" :
                                            "bg-gray-100 text-gray-500"
                                          }`}>
                                            {v.status || "draft"}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Links Tab */}
                        {detailTab === "links" && (
                          <div>
                            {c.links.length === 0 ? (
                              <div className="text-center py-8 text-gray-400">
                                <p>No affiliate links linked to this campaign.</p>
                                <Link
                                  to="/campaigns/affiliate-links"
                                  className="text-indigo-600 font-semibold hover:underline mt-2 inline-block"
                                >
                                  Manage Affiliate Links →
                                </Link>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {c.links.map((link) => (
                                  <div
                                    key={link.id}
                                    className={`flex items-center justify-between p-4 rounded-lg border ${
                                      link.is_active
                                        ? "bg-emerald-50/50 border-emerald-200"
                                        : "bg-gray-50 border-gray-200"
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <span className={`w-2 h-2 rounded-full ${link.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
                                      <div>
                                        <code className="text-sm font-semibold text-indigo-600">/go/{link.slug}/</code>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                          {link.network_display || link.network_name} · {link.geo || "—"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <div className="text-center">
                                        <div className="text-xs text-gray-400">7d clicks</div>
                                        <div className="font-semibold">{link.clicks_7d || 0}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-xs text-gray-400">30d clicks</div>
                                        <div className="font-semibold">{link.clicks_30d || 0}</div>
                                      </div>
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        link.is_active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"
                                      }`}>
                                        {link.is_active ? "Active" : "Inactive"}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Revenue Tab */}
                        {detailTab === "revenue" && (
                          <div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                              {[
                                { label: "Revenue (7d)", value: formatDKK(c.revenue7d), color: "text-emerald-600" },
                                { label: "Conversions (7d)", value: c.conversions7d, color: "text-indigo-600" },
                                { label: "Revenue (30d)", value: formatDKK(c.revenue30d), color: "text-emerald-600" },
                                { label: "Conversions (30d)", value: c.conversions30d, color: "text-indigo-600" },
                              ].map((m) => (
                                <div key={m.label} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                  <div className="text-xs text-gray-400 font-medium">{m.label}</div>
                                  <div className={`text-xl font-bold ${m.color}`}>
                                    {typeof m.value === "number" ? m.value.toLocaleString() : m.value}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                              <p className="font-medium text-gray-500 mb-1">WeCanTrack Matching</p>
                              <p>
                                Matched on advertiser names: {(c.wctAdvertiserNames || [c.advertiser, c.name]).map((n) => `"${n}"`).join(", ")}
                                {" "} · Geo: {c.geo || "all"}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* WCT Mapping Tab */}
                        {detailTab === "wct" && (
                          <WctAdvertiserPanel
                            campaign={c}
                            allAdvertisers={allWctAdvertisers}
                            onSave={async (updatedNames: string[]) => {
                              const updatedCampaign = { ...c, wctAdvertiserNames: updatedNames };
                              try {
                                await fetch(`${API_BASE_URL}/api/text-library/campaigns`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify(updatedCampaign),
                                });
                                setCampaigns((prev) =>
                                  prev.map((x) => (x.campaignId === c.campaignId ? { ...x, wctAdvertiserNames: updatedNames } : x))
                                );
                                setWctSaveMsg('Saved & synced to Postgres!');
                                setTimeout(() => setWctSaveMsg(''), 3000);
                              } catch (err) {
                                console.error('Failed to save WCT mappings:', err);
                              }
                            }}
                            saveMsg={wctSaveMsg}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create Campaign Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
            <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Create Campaign</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. Zmarta"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign((p) => ({ ...p, name: e.target.value, advertiser: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Geo</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={newCampaign.geo}
                      onChange={(e) => setNewCampaign((p) => ({ ...p, geo: e.target.value }))}
                    >
                      {GEOS.filter((g) => g.id !== "ALL").map((g) => (
                        <option key={g.id} value={g.id}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={newCampaign.category}
                      onChange={(e) => setNewCampaign((p) => ({ ...p, category: e.target.value }))}
                    >
                      {CATEGORIES.filter((c) => c !== "All").map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Landing Page URL</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="https://zmarta.se/forbrukslan"
                    value={newCampaign.landingPageUrl}
                    onChange={(e) => setNewCampaign((p) => ({ ...p, landingPageUrl: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    rows={3}
                    placeholder="Loan comparison service, up to 800,000 kr..."
                    value={newCampaign.description}
                    onChange={(e) => setNewCampaign((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newCampaign.name.trim() || saving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? "Creating..." : "Create Campaign"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Campaign Modal */}
        {editCampaign && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditCampaign(null)}>
            <div className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Edit Campaign</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign ID *</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-indigo-600 focus:ring-2 focus:ring-indigo-500"
                    value={editForm.campaignId}
                    onChange={(e) => setEditForm((p) => ({ ...p, campaignId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  />
                  <p className="text-xs text-gray-400 mt-1">Changing this will move all text versions to the new ID.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    value={editForm.advertiser}
                    onChange={(e) => setEditForm((p) => ({ ...p, advertiser: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Geo</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={editForm.geo}
                      onChange={(e) => setEditForm((p) => ({ ...p, geo: e.target.value }))}
                    >
                      {GEOS.filter((g) => g.id !== "ALL").map((g) => (
                        <option key={g.id} value={g.id}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={editForm.category}
                      onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value }))}
                    >
                      {CATEGORIES.filter((c) => c !== "All").map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🏷️ Brand Name (Display)</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 font-semibold"
                      placeholder="e.g. Lendo, Sambla"
                      value={editForm.brandName}
                      onChange={(e) => setEditForm((p) => ({ ...p, brandName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🏦 Loan / Product Type</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Låneförmedlare, Kontokredit"
                      value={editForm.loanType}
                      onChange={(e) => setEditForm((p) => ({ ...p, loanType: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Min Amount</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. 5 000 kr"
                      value={editForm.minLoanAmount}
                      onChange={(e) => setEditForm((p) => ({ ...p, minLoanAmount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Max Amount</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:ring-2 focus:ring-indigo-500 font-semibold text-emerald-700"
                      placeholder="e.g. 800 000 kr"
                      value={editForm.maxLoanAmount}
                      onChange={(e) => setEditForm((p) => ({ ...p, maxLoanAmount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Lenders / Banks</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Upp till 40"
                      value={editForm.lenderCount}
                      onChange={(e) => setEditForm((p) => ({ ...p, lenderCount: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">✨ Key USPs (comma-separated)</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. Endast 1 UC, Svar inom 1 minut, BankID"
                    value={editForm.keyUsps}
                    onChange={(e) => setEditForm((p) => ({ ...p, keyUsps: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">⚖️ Legal Loan Example (Räkneexempel)</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono bg-slate-50 text-slate-700"
                    rows={3}
                    placeholder="Ränteexempel: Vid ett lån på..."
                    value={editForm.loanExample}
                    onChange={(e) => setEditForm((p) => ({ ...p, loanExample: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Landing Page URL</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://example.com"
                    value={editForm.landingPageUrl}
                    onChange={(e) => setEditForm((p) => ({ ...p, landingPageUrl: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    rows={2}
                    value={editForm.description}
                    onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditCampaign(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={!editForm.name.trim() || editSaving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {editSaving ? "Saving..." : "💾 Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ═══ MODAL: Backfill from ESPs ═══ */}
        {showBackfill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.35)' }}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">🔄 Backfill Text Library from ESPs</h3>
                <button onClick={() => setShowBackfill(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>

              {backfillLoading && (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 mt-3">{backfillResult ? 'Importing...' : 'Scanning ESP tables...'}</p>
                </div>
              )}

              {backfillResult?.error && (
                <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">❌ {backfillResult.error}</div>
              )}

              {backfillResult?.stats && !backfillLoading && (
                <div className="space-y-4">
                  {/* Stats summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[{ label: 'New Campaigns', value: backfillResult.stats.campaigns_created, color: 'indigo' },
                      { label: 'Text Versions', value: backfillResult.stats.versions_imported, color: 'emerald' },
                      { label: 'Already Exist', value: backfillResult.stats.skipped_existing, color: 'gray' },
                      { label: 'Empty (skipped)', value: backfillResult.stats.skipped_empty, color: 'gray' },
                    ].map((s) => (
                      <div key={s.label} className={`rounded-lg p-3 bg-${s.color}-50 text-center`}>
                        <div className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</div>
                        <div className="text-xs text-gray-500 mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="text-xs text-gray-400">
                    Scanned {backfillResult.stats.esp_tables_scanned} ESP tables, found {backfillResult.stats.total_esp_campaigns} total campaigns
                  </div>

                  {/* Details */}
                  {backfillResult.details?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Campaigns to Import</span>
                        {backfillResult.dryRun && !backfillDone && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedBackfill(new Set(backfillResult.details.map((d: any) => d.campaignId)))}
                              className="text-xs text-indigo-600 hover:underline font-medium"
                            >Select All</button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={() => setSelectedBackfill(new Set())}
                              className="text-xs text-gray-500 hover:underline font-medium"
                            >None</button>
                            <span className="text-xs text-gray-400 ml-1">{selectedBackfill.size} selected</span>
                          </div>
                        )}
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                        {backfillResult.details.map((d: any) => (
                          <div key={d.campaignId} className={`px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50 ${selectedBackfill.has(d.campaignId) ? 'bg-indigo-50/50' : ''}`}>
                            {backfillResult.dryRun && !backfillDone && (
                              <input
                                type="checkbox"
                                checked={selectedBackfill.has(d.campaignId)}
                                onChange={(e) => {
                                  const next = new Set(selectedBackfill);
                                  if (e.target.checked) next.add(d.campaignId);
                                  else next.delete(d.campaignId);
                                  setSelectedBackfill(next);
                                }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                            )}
                            <div className="flex-1 flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-800">{d.name}</span>
                                <span className="text-xs text-gray-400 ml-2">{d.geo}</span>
                                {d.isNew && <span className="text-xs text-indigo-600 ml-2 font-semibold">NEW</span>}
                              </div>
                              <div className="text-xs text-gray-500">
                                {d.versionsToImport?.length || 0} versions · from {d.espSources?.join(', ')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2">
                    {backfillProgress && <span className="text-xs text-indigo-600 font-medium">{backfillProgress}</span>}
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={() => setShowBackfill(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        {backfillDone ? 'Close' : 'Cancel'}
                      </button>
                      {!backfillDone && backfillResult.dryRun && selectedBackfill.size > 0 && (
                        <button
                          onClick={async () => {
                            setBackfillLoading(true);
                            const selected = backfillResult.details.filter((d: any) => selectedBackfill.has(d.campaignId));
                            let imported = 0;
                            for (const d of selected) {
                              setBackfillProgress(`Importing ${d.name} ${d.geo}... (${imported + 1}/${selected.length})`);
                              try {
                                await fetch(`${API_BASE_URL}/api/text-library/bulk-import`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ dryRun: false, geos: ['NO', 'SE', 'FI', 'DE'], campaign: d.name }),
                                });
                                imported++;
                              } catch (err) {
                                console.error(`Failed to import ${d.name}:`, err);
                              }
                            }
                            setBackfillProgress(`Done! Imported ${imported} campaigns.`);
                            setBackfillDone(true);
                            setBackfillLoading(false);
                            loadData();
                          }}
                          disabled={backfillLoading}
                          className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          ✅ Import {selectedBackfill.size} Campaign{selectedBackfill.size !== 1 ? 's' : ''}
                        </button>
                      )}
                      {!backfillDone && backfillResult.dryRun && selectedBackfill.size === 0 && backfillResult.stats.versions_imported > 0 && (
                        <span className="text-xs text-gray-400 self-center">Select campaigns to import ↑</span>
                      )}
                      {backfillDone && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600 font-semibold">
                          ✅ Import complete!
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
